import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Chess} from 'chess.js';
import StockfishEngine from '@/Components/StockfishEngine';
import type {CevalPvLine} from '@/Components/CevalPanel';
import type {AutoShape} from '@/Components/ChessboardWithArrow/EngineShapesOverlay';
import {getLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';
import {sanitizeFen, winningChances} from '../utils/studyHelpers';

// Local ceval search budget. The engine layer additionally caps each search
// with a 10s movetime watchdog, so this is the "Depth n/24" ceiling the
// panel reports, not a promise a slow device must reach.
export const CEVAL_MAX_DEPTH = 24;

export type StockfishDisplayResult = {
  score: string;
  isMate: boolean;
  baseTurn: 'w' | 'b';
  rawScore: string;
};

type UseEngineAnalysisArgs = {
  analysisFen: string;
  // Resets threat mode and PV state when the chapter switches.
  chapterId: any;
  // Locked lesson/practice chapters must not leak best-move/threat arrows
  // to the student even while the engine runs for the practice opponent.
  isChapterLocked: boolean;
  // Practice mode always runs a fixed single-line budget, matching
  // Lichess's studyPracticeCtrl (multiPv: 1) - it must not follow the
  // student's own Analysis-tab MultiPV preference.
  practiceMode: boolean;
  // Top-line result callback, fired once per depth on the primary PV -
  // the screen uses it for the eval bar and practice-verdict
  // classification. `null` means the engine was turned off.
  onResult: (result: StockfishDisplayResult | null) => void;
};

// Lichess-style local eval (ceval): the main engine always searches the
// real position; threat mode ("x") runs a second, independent search with
// the move passed to the opponent, so the best-move arrow and the threat
// arrow can be shown at the same time. The engines run headlessly at the
// screen level (see cevalEngineElement/threatEngineElement) so practice
// mode keeps its opponent even when the Analysis tab isn't mounted;
// CevalPanel is purely presentational.
export function useEngineAnalysis({
  analysisFen,
  chapterId,
  isChapterLocked,
  practiceMode,
  onResult,
}: UseEngineAnalysisArgs) {
  const [enableStockfish, setEnableStockfish] = useState(false);
  const [cevalPvs, setCevalPvs] = useState<CevalPvLine[]>([]);
  const [cevalDepth, setCevalDepth] = useState(0);
  // Threat mode's own independent PV array - its lines never overwrite the
  // real position's cevalPvs above; that overwrite was why the own-best-
  // move arrow used to disappear whenever the threat arrow was turned on.
  const [threatPvs, setThreatPvs] = useState<CevalPvLine[]>([]);
  // "Multiple lines" (MultiPV) + "Best move arrow" — the two engine
  // preferences lichess exposes; persisted like the explorer config.
  const [cevalMultiPv, setCevalMultiPv] = useState(1);
  const [showBestMoveArrow, setShowBestMoveArrow] = useState(true);
  const [threatMode, setThreatMode] = useState(false);
  // First move of a PV row the user is hovering — previewed as an arrow.
  const [hoveredPvUci, setHoveredPvUci] = useState<string | null>(null);

  // Threat mode analyzes the current position with the turn handed to the
  // opponent (en passant cleared — it's no longer meaningful after a null
  // move). Unavailable while in check or at game over, where a null move
  // makes no sense; lichess hides threat the same way.
  const threatFen = useMemo(() => {
    const realFen = sanitizeFen(analysisFen);
    try {
      const chess = new Chess(realFen);
      if (chess.isCheck() || chess.isGameOver()) {
        return null;
      }
    } catch (e) {
      return null;
    }
    const parts = realFen.split(' ');
    if (parts.length < 2) {
      return null;
    }
    parts[1] = parts[1] === 'w' ? 'b' : 'w';
    if (parts.length > 3) {
      parts[3] = '-';
    }
    return parts.join(' ');
  }, [analysisFen]);

  const effectiveThreatMode = threatMode && !!threatFen && enableStockfish;
  const cevalFen = sanitizeFen(analysisFen);

  const cevalGameOver = useMemo(() => {
    try {
      return new Chess(sanitizeFen(analysisFen)).isGameOver();
    } catch (e) {
      return false;
    }
  }, [analysisFen]);

  // Deliberately does NOT clear cevalPvs when cevalFen/threat/multiPv change:
  // the engine's own search-token filtering (engine.ts) already guarantees
  // every line we receive belongs to the CURRENT search, so a stale line
  // from the previous position can never leak in. Blanking the array here
  // instead just makes the board arrow (and eval) flash to nothing for the
  // ~100ms-plus it takes the fresh search to report its first line -
  // most noticeably when toggling threat mode, where it looked like the
  // arrow "didn't show" at all. Leaving the last-known line in place until
  // it's naturally overwritten is what lichess's own ceval display does too.
  useEffect(() => {
    if (!enableStockfish) {
      setCevalPvs([]);
      setCevalDepth(0);
      setThreatPvs([]);
      setHoveredPvUci(null);
    }
  }, [enableStockfish]);

  useEffect(() => {
    setThreatMode(false);
    setCevalPvs([]);
    setCevalDepth(0);
    setThreatPvs([]);
  }, [chapterId]);

  // Restore the user's engine preferences once, then persist changes.
  const cevalConfigLoadedRef = useRef(false);
  useEffect(() => {
    (async () => {
      const stored = (await getLocalStorage('study:ceval-config')) as {
        multiPv?: number;
        showBestMoveArrow?: boolean;
      } | null;
      if (stored) {
        if (
          typeof stored.multiPv === 'number' &&
          stored.multiPv >= 1 &&
          stored.multiPv <= 5
        ) {
          setCevalMultiPv(stored.multiPv);
        }
        if (typeof stored.showBestMoveArrow === 'boolean') {
          setShowBestMoveArrow(stored.showBestMoveArrow);
        }
      }
      cevalConfigLoadedRef.current = true;
    })();
  }, []);
  useEffect(() => {
    if (cevalConfigLoadedRef.current) {
      setLocalStorage('study:ceval-config', {
        multiPv: cevalMultiPv,
        showBestMoveArrow,
      });
    }
  }, [cevalMultiPv, showBestMoveArrow]);

  // One `info` line from the engine -> one PV row. StockfishEngine keeps its
  // onResult ref fresh every render, so this closure always sees current
  // state without a useCallback dependency dance.
  const handleCevalInfo = (
    variationNumber: number,
    movesSAN: string[],
    baseTurn: 'w' | 'b',
    score: string,
    isMate: boolean,
    raw: string,
  ) => {
    const depthMatch = raw.match(/\bdepth (\d+)/);
    const lineDepth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
    const pvIdx = raw.indexOf(' pv ');
    const uciMoves =
      pvIdx === -1
        ? []
        : raw
            .slice(pvIdx + 4)
            .trim()
            .split(/\s+/);
    if (!movesSAN.length || !uciMoves.length) {
      return;
    }
    // Standard UCI scores are side-to-move POV; store white POV.
    const sign = baseTurn === 'b' ? -1 : 1;
    const numeric = Number(score) || 0;
    const line: CevalPvLine = {
      sanMoves: movesSAN,
      uciMoves,
      depth: lineDepth,
      ...(isMate ? {mate: sign * numeric} : {cp: sign * numeric}),
    };
    setCevalPvs(prev => {
      const next = prev.slice(0, cevalMultiPv);
      next[variationNumber - 1] = line;
      return next;
    });
    if (variationNumber !== 1) {
      return;
    }
    setCevalDepth(lineDepth);
    let displayScore: string;
    if (isMate) {
      displayScore = numeric === 0 ? '#' : `M${Math.abs(numeric)}#`;
    } else {
      let cp = numeric;
      if (baseTurn === 'b') {
        cp = -cp;
      }
      displayScore = (cp / 100).toFixed(2);
    }
    onResult({score: displayScore, isMate, baseTurn, rawScore: score});
  };

  // Threat mode's own `info` line handler — a completely independent PV
  // array from handleCevalInfo/cevalPvs above, so the threat search (run by
  // threatEngineElement against threatFen) never overwrites the real
  // position's best-move line.
  const handleThreatCevalInfo = (
    variationNumber: number,
    movesSAN: string[],
    baseTurn: 'w' | 'b',
    score: string,
    isMate: boolean,
    raw: string,
  ) => {
    const depthMatch = raw.match(/\bdepth (\d+)/);
    const lineDepth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
    const pvIdx = raw.indexOf(' pv ');
    const uciMoves =
      pvIdx === -1
        ? []
        : raw
            .slice(pvIdx + 4)
            .trim()
            .split(/\s+/);
    if (!movesSAN.length || !uciMoves.length) {
      return;
    }
    const sign = baseTurn === 'b' ? -1 : 1;
    const numeric = Number(score) || 0;
    const line: CevalPvLine = {
      sanMoves: movesSAN,
      uciMoves,
      depth: lineDepth,
      ...(isMate ? {mate: sign * numeric} : {cp: sign * numeric}),
    };
    setThreatPvs(prev => {
      const next = prev.slice(0, 1);
      next[variationNumber - 1] = line;
      return next;
    });
  };

  // Best move of the real position's top line — practice mode's engine reply
  // and move classification hang off this.
  const nextBestMoveUci = useMemo(
    () => cevalPvs[0]?.uciMoves?.[0] || null,
    [cevalPvs],
  );

  const toggleThreatMode = useCallback(() => {
    if (!enableStockfish) {
      return;
    }
    setThreatMode(prev => {
      const next = !prev;
      if (!next) {
        setThreatPvs([]);
      }
      return next;
    });
  }, [enableStockfish]);

  const handleCevalToggle = (value: boolean) => {
    setEnableStockfish(value);
    if (!value) {
      onResult(null);
      setThreatMode(false);
      setThreatPvs([]);
      setHoveredPvUci(null);
    }
  };

  // Engine arrows, lichess-style. The normal best-move arrow (blue, from
  // cevalPvs — always the real position) and the threat arrow (red, from
  // threatPvs — the independent threat-mode search) are drawn from two
  // separate PV arrays, so turning threat mode on adds the red arrow
  // alongside the blue one instead of replacing it. A hovered PV row
  // previews at full strength on top of both.
  const engineAutoShapes = useMemo((): AutoShape[] => {
    // Practice mode force-enables enableStockfish internally so the
    // computer opponent can move, but the resulting best-move/threat
    // arrows are exactly the kind of hint a locked lesson/practice chapter
    // shouldn't leak to the student - so also gate on isChapterLocked
    // here, not just enableStockfish.
    if (!enableStockfish || isChapterLocked) {
      return [];
    }
    const shapes: AutoShape[] = [];
    const seen = new Set<string>();
    const topUci = cevalPvs[0]?.uciMoves?.[0];

    if (showBestMoveArrow && topUci && topUci.length >= 4) {
      const key = topUci.slice(0, 4);
      seen.add(key);
      shapes.push({
        orig: topUci.slice(0, 2),
        dest: topUci.slice(2, 4),
        brush: 'paleBlue',
      });

      const bestWc = winningChances(cevalPvs[0]);
      cevalPvs.slice(1).forEach(pv => {
        const uci = pv?.uciMoves?.[0];
        if (!uci || uci.length < 4) {
          return;
        }
        const altKey = uci.slice(0, 4);
        if (seen.has(altKey)) {
          return;
        }
        seen.add(altKey);
        const gap = Math.abs(bestWc - winningChances(pv));
        shapes.push({
          orig: uci.slice(0, 2),
          dest: uci.slice(2, 4),
          brush: 'paleBlue',
          opacityFactor: Math.max(0.2, 1 - gap * 2.5),
        });
      });
    }

    if (effectiveThreatMode) {
      const threatUci = threatPvs[0]?.uciMoves?.[0];
      if (threatUci && threatUci.length >= 4) {
        const key = threatUci.slice(0, 4);
        if (!seen.has(key)) {
          seen.add(key);
          shapes.push({
            orig: threatUci.slice(0, 2),
            dest: threatUci.slice(2, 4),
            brush: 'paleRed',
          });
        }
      }
    }

    if (hoveredPvUci && hoveredPvUci.length >= 4) {
      const key = hoveredPvUci.slice(0, 4);
      if (!seen.has(key)) {
        shapes.push({
          orig: hoveredPvUci.slice(0, 2),
          dest: hoveredPvUci.slice(2, 4),
          brush: effectiveThreatMode ? 'paleRed' : 'paleBlue',
        });
      }
    }
    return shapes;
  }, [
    enableStockfish,
    isChapterLocked,
    showBestMoveArrow,
    cevalPvs,
    effectiveThreatMode,
    threatPvs,
    hoveredPvUci,
  ]);

  // The engines themselves: headless (render null), mounted in both layouts
  // so eval / practice keep running whichever tab is visible.
  const cevalEngineElement = (
    <StockfishEngine
      fen={cevalFen}
      depth={CEVAL_MAX_DEPTH}
      variations={practiceMode ? 1 : cevalMultiPv}
      engineOn={enableStockfish && !cevalGameOver}
      onResult={handleCevalInfo}
    />
  );

  // Second, independent engine instance dedicated to threat mode — only
  // runs while threat mode is actually on, searching the opponent's reply
  // position (threatFen) without touching cevalFen/cevalPvs above.
  const threatEngineElement = (
    <StockfishEngine
      fen={threatFen || cevalFen}
      depth={CEVAL_MAX_DEPTH}
      variations={1}
      engineOn={effectiveThreatMode && !cevalGameOver}
      onResult={handleThreatCevalInfo}
    />
  );

  // "x" toggles threat mode, matching lichess's analysis keymap.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        return;
      }
      if (event.key === 'x' || event.key === 'X') {
        toggleThreatMode();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleThreatMode]);

  return {
    enableStockfish,
    setEnableStockfish,
    cevalFen,
    threatFen,
    cevalPvs,
    cevalDepth,
    cevalMultiPv,
    setCevalMultiPv,
    showBestMoveArrow,
    setShowBestMoveArrow,
    effectiveThreatMode,
    toggleThreatMode,
    handleCevalToggle,
    setHoveredPvUci,
    nextBestMoveUci,
    engineAutoShapes,
    cevalEngineElement,
    threatEngineElement,
  };
}
